/**
 * Central storage helpers for FleetSkipper
 * Abstracts localStorage access and emits events for cloud sync
 *
 * CRITICAL: This abstraction enables seamless cloud migration
 * All modules should use these helpers instead of direct localStorage
 *
 * Events emitted:
 * - sr:storage:set - When data is written (for UniversalSyncBridge to intercept)
 */

import { FLAGS } from '../config/featureFlags';
import { getOrgId } from '../utils/getOrgId';
import * as storageIDB from './storageIndexedDB';

// FT-324 (2026-07-06): Pre-compiled wormhole regex — see comment inside extractModule.
// Hoisted to module scope so it isn't recompiled on every write.
const FT324_UUID_RE = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const FT324_LEGACY_VESSEL_RE = '[A-Z]{2,4}[-\\d]+[-\\d]*';
const FT324_CHECKLIST_WORMHOLE_RE = new RegExp(
  `^checklist_(?:${FT324_UUID_RE}|${FT324_LEGACY_VESSEL_RE})(?:_(?:${FT324_UUID_RE}|${FT324_LEGACY_VESSEL_RE}))?$`,
  'i'
);

/**
 * Extract module name from storage key
 * Used for routing data to correct cloud collection
 * @param {string} key - localStorage key
 * @returns {string} Module name
 */
const extractModule = (key) => {
  // FT-05 FIX: Org-scoped modules (no vessel ID suffix)
  // These modules store data at org level, so the key IS the module name
  if (key === 'fleet_crew' || key.startsWith('fleet_crew_')) return 'fleet_crew';
  // FT-116 FIX: Fleet-wide risk assessments (org-scoped)
  if (key === 'fleet_risk_assessments' || key.startsWith('fleet_risk_assessments_')) return 'fleet_risk_assessments';

  // FT-15 FIX: CRITICAL - crew_familiarisations_ MUST come BEFORE generic crew_ fallback
  if (key.startsWith('crew_familiarisations_')) return 'crew_familiarisations';
  if (key.startsWith('crew_')) return 'crew';  // FIXED 2026-02-10: Match MODULE_MAP key
  if (key.startsWith('equipment_register_')) return 'equipment_register';  // FIXED 2026-02-13: Match MODULE_MAP key (Bug #1 v4.2)
  if (key.startsWith('equipment_records_')) return 'equipment_register';  // Legacy key alias
  if (key.startsWith('equipment_selections_')) return 'equipment_selections';  // FT-10 FIX: User equipment selections from wizard
  // FT-86: custom_maintenance_jobs removed - fully migrated to maintenance_jobs (2026-03-28)
  if (key.startsWith('maintenance_schedule_')) return 'maintenance_schedule';  // FIXED 2026-02-16: Separate mapping (Option 2)
  if (key.startsWith('maintenance_jobs_')) return 'maintenance_jobs';  // FIXED 2026-02-16: Separate mapping (Option 2) - job definitions
  if (key.startsWith('maintenance_history_')) return 'maintenance_history';  // Completion records only
  if (key.startsWith('maintenance_config_')) return 'maintenance_config';  // FT-04 FIX: advanceWarningDays, visibleCategories sync
  if (key.startsWith('job_descriptions_')) return 'job_descriptions';  // FT-137: Custom job descriptions (per job ID)
  if (key.startsWith('drills_config_')) return 'drills_config';  // FT-112c: MUST come before drill_ - companyRequiredDrills, drillFrequencies
  if (key.startsWith('drill_settings_')) return 'drill_settings';  // FT-11 FIX: MUST come before drill_ - drill frequencies, enabled flags
  if (key.startsWith('drill_register_')) return 'drill_register';  // FT-406 FIX: MUST come before drill_ - per-vessel drill register ledger (else collides with 'drills'/sr.drills.v1)
  if (key.startsWith('drill_')) return 'drills';
  // FT-114: Config keys MUST come before records keys to prevent mis-routing
  if (key.startsWith('defects_config_')) return 'defects_config';  // FT-114: Defects settings sync
  if (key.startsWith('defects_')) return 'defects';
  if (key.startsWith('incidents_config_')) return 'incidents_config';  // FT-114: Incidents settings sync
  if (key.startsWith('incidents_')) return 'incidents';
  if (key.startsWith('risk_assessment_config_')) return 'risk_assessment_config';  // FT-114: RA settings sync
  if (key.startsWith('risk_')) return 'risk_assessments';  // FIXED 2026-02-23: Match MODULE_MAP key (underscore)
  if (key.startsWith('checklist_configs_')) return 'checklist_configs';  // FT-114: Checklist settings sync
  if (key.startsWith('voyage_settings_')) return 'voyage_settings';  // Phase 5 (2026-03-13): MUST come before voyage_
  if (key.startsWith('voyage_phases_')) return 'voyage_phases';  // FT-461 FIX: MUST come before voyage_ - per-vessel voyage phase-state OBJECT (else collides with 'voyages'/sr.voyages.v1)
  if (key.startsWith('voyage_')) return 'voyages';
  if (key.startsWith('daily_checks_')) return 'daily_checks';
  // FT-164 FIX: Handle org-scoped HOR key (no vessel suffix) — same pattern as fleet_crew line 25
  if (key === 'hours_of_rest' || key.startsWith('hours_of_rest_')) return 'hours_of_rest';
  if (key.startsWith('vesselSettings_')) return 'vesselSettings';
  // PATTERN ORDER CRITICAL: Specific operations_ prefixes MUST come before generic fallback
  if (key.startsWith('operations_daily_checks_config_')) return 'operations_daily_checks_config';  // FT-113: Daily checks config sync
  if (key.startsWith('operations_main_logbook_')) return 'operations_main_logbook';  // FIXED 2026-02-24: Bug 2
  if (key.startsWith('operations_voyages_')) return 'operations_voyages';  // FIXED 2026-02-24: Bug 2
  // FIXED 2026-02-27: Operations sub-modules — SPECIFIC FIRST, GENERIC LAST
  if (key.startsWith('operations_checklist_templates_')) return 'operations_checklist_templates';
  if (key.startsWith('operations_checklist_favourites_')) return 'operations_checklist_favourites';
  // FT-354d (2026-07-10): nav_favourites_ prefix rule removed with the favourites feature.
  if (key.startsWith('operations_checklists_')) return 'operations_checklists';
  if (key.startsWith('operations_permits_')) return 'operations_permits';
  if (key.startsWith('operations_bunkering_')) return 'operations_bunkering';
  if (key.startsWith('operations_voyage_templates_')) return 'operations_voyage_templates';  // Phase 3: Voyage templates sync
  // FIXED 2026-02-27: Special operations
  if (key.startsWith('pilot_transfers_')) return 'pilot_transfers';
  if (key.startsWith('towing_operations_')) return 'towing_operations';
  if (key.startsWith('diving_operations_')) return 'diving_operations';
  // FIXED 2026-02-27: Checklist records (note: actual key uses 'seaready_checklist_records')
  // FT-324 (2026-07-06): 'seaready_checklist_records_*' kept as migration compatibility path —
  // useChecklistRecords now writes to 'checklist_records_*' canonically, but the alias may still
  // be present on devices mid-migration.
  if (key.startsWith('seaready_checklist_records_')) return 'checklist_records';
  if (key.startsWith('checklist_records_')) return 'checklist_records';
  // FT-324 (2026-07-06): Explicit resolution for the pull-side write path
  // `checklist_${vesselId}` (also `checklist_${orgId}_${vesselId}` after org-scoping) that the
  // sync bridge historically produced via the now-removed 'checklist' MODULE_MAP alias.
  // Without this rule the generic underscore fallback below would return 'checklist', which
  // routed pushes to a ghost cloud doc `${vesselId}_checklist` alongside the real
  // `${vesselId}_checklist_records`. Rule must come BEFORE the generic fallback and must NOT
  // match legitimate `checklist_records_*` / `checklist_configs_*` prefixes (already handled
  // above). Matches both plain vessel-suffix and org-scoped forms. UUID + legacy vessel ID.
  if (FT324_CHECKLIST_WORMHOLE_RE.test(key)) return 'checklist_records';
  // PATTERN ORDER CRITICAL: Specific prefix MUST come before broad prefix
  // Otherwise 'familiarisation_records_' would match 'familiarisation_' first
  // FT-15 FIX: familiarisation_checklist_templates_ MUST come before familiarisation_records_
  if (key.startsWith('familiarisation_checklist_templates_')) return 'familiarisation_checklist_templates';
  if (key.startsWith('familiarisationSettings_')) return 'familiarisationSettings';  // FT-15 FIX: Settings sync
  if (key.startsWith('familiarisation_records_')) return 'familiarisation_records';
  if (key.startsWith('familiarisation_progress_')) return 'familiarisation_progress';  // FT-19: Draft progress sync
  if (key.startsWith('documents_')) return 'documents';
  if (key.startsWith('certificates_')) return 'certificates';
  if (key.startsWith('log_entries_')) return 'log_entries';  // Bug #4 fix (2026-02-13): Enable logbook sync
  if (key.startsWith('onboard_training_')) return 'onboard_training';  // Bug #4 fix (2026-02-13): Enable training sync
  if (key.startsWith('audits_')) return 'audits';  // Days 4-5 v4.2
  if (key.startsWith('audit_log_')) return 'audit_log';  // Days 16-17 v4.2: Module audit trails
  if (key.startsWith('compliance_items_')) return 'compliance_items';  // Days 4-5 v4.2
  if (key.startsWith('compliance_register_')) return 'compliance_register';  // FT-122: Compliance Register sync
  if (key.startsWith('sms_documents_')) return 'sms_documents';  // Days 4-5 v4.2
  // Return the key prefix before underscore, or the whole key
  const underscoreIndex = key.indexOf('_');
  return underscoreIndex > 0 ? key.substring(0, underscoreIndex) : key;
};

/**
 * Extract vessel ID from storage key
 * @param {string} key - localStorage key (e.g., 'crew_VSL001-25')
 * @returns {string} Vessel ID or 'default'
 */
const extractVesselId = (key) => {
  // Split by underscore and check segments right-to-left
  // This ensures we get the vessel ID, not the org ID in org-scoped keys
  const segments = key.split('_');

  // Check segments from right to left
  for (let i = segments.length - 1; i >= 0; i--) {
    const segment = segments[i];

    // Match UUID pattern: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
      return segment;
    }

    // Match legacy vessel ID pattern: VSL001-25, VSL-001, etc.
    if (/^[A-Z]{2,4}[-\d]+[-\d]*$/i.test(segment)) {
      return segment;
    }
  }

  return 'default';
};

/**
 * FT-168: Org-scoped modules (no vessel suffix) — need org prefix appended
 * These modules use noScope: true in useStorageState, storing data at org level
 * WITHOUT this list, they all share same localStorage key across orgs!
 */
const ORG_SCOPED_MODULES = [
  'fleet_crew',
  'fleet_risk_assessments',
  'hours_of_rest'
];

/**
 * Transform a vessel-only key to org-scoped key if ORG_SCOPED_STORAGE is enabled
 * @param {string} key - Storage key (e.g., 'maintenance_jobs_VSL001')
 * @returns {string} Transformed key (e.g., 'maintenance_jobs_ORG123_VSL001')
 */
export const toOrgScopedKey = (key) => {
  if (!FLAGS.ORG_SCOPED_STORAGE) {
    return key;
  }

  const orgId = getOrgId();
  // SINGLE ORG-SCOPING FIX (2026-02-22): Reject 'default-org' as invalid
  if (!orgId || orgId === 'default-org') {
    console.warn('[Storage] toOrgScopedKey: No valid orgId available, using key as-is');
    return key;
  }

  if (key.includes(orgId)) {
    return key;
  }

  // FT-168 FIX: Handle org-scoped modules (no vessel suffix)
  // These keys are JUST the module name (e.g., 'fleet_crew', 'hours_of_rest')
  // They need org prefix appended: 'fleet_crew' → 'fleet_crew_<orgId>'
  if (ORG_SCOPED_MODULES.includes(key)) {
    const orgScopedKey = `${key}_${orgId}`;
    return orgScopedKey;
  }

  const parts = key.split('_');
  if (parts.length < 2) {
    return key;
  }

  const lastPart = parts[parts.length - 1];
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lastPart);
  const isLegacyVesselId = /^[A-Z]{2,4}[-\d]+[-\d]*$/i.test(lastPart);

  if (!isUUID && !isLegacyVesselId) {
    return key;
  }

  const moduleParts = parts.slice(0, -1).join('_');
  const vesselId = lastPart;
  const orgScopedKey = `${moduleParts}_${orgId}_${vesselId}`;

  return orgScopedKey;
};

/**
 * Read JSON from storage with fallback
 * PHASE 2 MULTI-TENANCY: Auto-transforms to org-scoped key and handles migration
 */
export const readJSON = (key, fallback = null) => {
  try {
    const orgScopedKey = toOrgScopedKey(key);
    let value = localStorage.getItem(orgScopedKey);

    if (value) {
      return JSON.parse(value);
    }

    if (FLAGS.ORG_SCOPED_STORAGE && orgScopedKey !== key) {
      value = localStorage.getItem(key);
      if (value) {
        localStorage.setItem(orgScopedKey, value);
        return JSON.parse(value);
      }
    }

    return fallback;
  } catch (error) {
    console.error('[Storage] Read error:', key, error);
    return fallback;
  }
};

/**
 * Write JSON to storage AND emit event for sync
 * PHASE 2 MULTI-TENANCY: Auto-transforms to org-scoped key
 */
export const writeJSON = (key, value, source = undefined) => {
  try {
    const storageKey = toOrgScopedKey(key);

    localStorage.setItem(storageKey, JSON.stringify(value));

    const module = extractModule(storageKey);
    const eventDetail = {
      module: module,
      vesselId: extractVesselId(storageKey),
      key: storageKey,
      value: value,
      timestamp: Date.now(),
      // FT-457: provenance tag (see storageIndexedDB.writeJSON). Present only
      // for cloud download/hydration echoes so the justPulled cooldown drops
      // the echo and never a genuine user write. Prod runs the IDB path
      // (PARALLEL_STORAGE:false) so this LS path is dead in prod, but the tag
      // is threaded here for parity + tests.
      ...(source !== undefined ? { source } : {})
    };

    window.dispatchEvent(new CustomEvent('sr:storage:set', {
      detail: eventDetail
    }));

    return true;
  } catch (error) {
    console.error('[Storage] Write error:', key, error);
    return false;
  }
};

/**
 * Remove item from storage
 * PHASE C FIX: Now uses org-scoped key and removes from IndexedDB too
 */
export const removeItem = (key) => {
  try {
    const storageKey = toOrgScopedKey(key);
    localStorage.removeItem(storageKey);

    // Remove from IndexedDB in parallel (async, non-blocking)
    storageIDB.removeItem(storageKey).catch(err => {
      console.warn('[Storage] removeItem: IndexedDB removal failed:', err);
    });

    return true;
  } catch (error) {
    console.error('[Storage] Remove error:', key, error);
    return false;
  }
};

/**
 * Check if key exists
 */
export const hasKey = (key) => {
  return localStorage.getItem(key) !== null;
};

/**
 * Get all keys matching pattern
 */
export const getKeys = (pattern) => {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!pattern || key.includes(pattern)) {
      keys.push(key);
    }
  }
  return keys;
};

/**
 * Clear all storage (use with caution)
 */
export const clearAll = () => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {

    return false;
  }
};

/**
 * Get storage size info
 */
export const getStorageInfo = () => {
  let totalSize = 0;
  const items = {};
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    const value = localStorage.getItem(key);
    const size = new Blob([value]).size;
    totalSize += size;
    items[key] = size;
  }
  
  return {
    totalSize,
    itemCount: localStorage.length,
    items
  };
};